import type { CatalogItem } from '@/lib/api/types.js';
import { t } from '@/lib/i18n/messages.js';
import { cn } from '@/lib/utils.js';
import { Link } from '@tanstack/react-router';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function interpolate(template: string, values: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (_, key: string) => String(values[key] ?? ''));
}

function formatRelativeTime(isoString: string): string {
  const diffMs = Date.now() - new Date(isoString).getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return t('catalog.ago_just_now');
  if (diffMin < 60) return interpolate(t('catalog.ago_minutes'), { n: diffMin });
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return interpolate(t('catalog.ago_hours'), { n: diffH });
  const diffD = Math.floor(diffH / 24);
  return interpolate(t('catalog.ago_days'), { n: diffD });
}

function KindBadge({ kind }: { kind: 'report' | 'dashboard' }) {
  const label = kind === 'report' ? 'Informe' : 'Dashboard';
  const colorClass =
    kind === 'report'
      ? 'bg-[var(--color-qup-info)]/15 text-[var(--color-qup-info)]'
      : 'bg-[var(--color-qup-warning)]/15 text-[var(--color-qup-warning)]';
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium',
        colorClass,
      )}
    >
      {label}
    </span>
  );
}

function CategoryBadge({ category }: { category: string }) {
  return (
    <span className="inline-flex items-center rounded-full bg-[var(--color-qup-border-dark)]/60 px-2 py-0.5 text-[11px] font-medium text-[var(--color-qup-text-muted-dark)]">
      {category}
    </span>
  );
}

function CapabilityBadge({ level }: { level: string }) {
  const label =
    level === 'manage'
      ? t('catalog.capability_manage')
      : level === 'execute'
        ? t('catalog.capability_execute')
        : level === 'view'
          ? t('catalog.capability_view')
          : level;

  const colorClass =
    level === 'manage'
      ? 'bg-[var(--color-qup-primary)]/15 text-[var(--color-qup-primary)]'
      : level === 'execute'
        ? 'bg-[var(--color-qup-success)]/15 text-[var(--color-qup-success)]'
        : 'bg-[var(--color-qup-border-dark)]/60 text-[var(--color-qup-text-muted-dark)]';
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium',
        colorClass,
      )}
    >
      {label}
    </span>
  );
}

function VersionUpdateBadge() {
  return (
    <span className="inline-flex items-center rounded-full bg-[var(--color-qup-warning)]/15 px-2 py-0.5 text-[11px] font-medium text-[var(--color-qup-warning)]">
      {t('catalog.version_update_available')}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Favorite button
// ---------------------------------------------------------------------------

interface FavoriteButtonProps {
  isFavorite: boolean;
  onToggle: () => void;
  isPending: boolean;
}

function FavoriteButton({ isFavorite, onToggle, isPending }: FavoriteButtonProps) {
  const label = isFavorite ? 'Quitar de favoritos' : 'Añadir a favoritos';
  return (
    <button
      type="button"
      aria-pressed={isFavorite ? 'true' : 'false'}
      aria-label={label}
      title={label}
      disabled={isPending}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onToggle();
      }}
      className={cn(
        'flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full transition-all duration-200',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-qup-primary)]',
        'hover:bg-[var(--color-qup-primary)]/10 disabled:opacity-50',
      )}
    >
      {isFavorite ? (
        <svg
          aria-hidden="true"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="var(--color-qup-primary)"
          stroke="var(--color-qup-primary)"
          strokeWidth="2"
        >
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
        </svg>
      ) : (
        <svg
          aria-hidden="true"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--color-qup-text-muted-dark)"
          strokeWidth="2"
        >
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
        </svg>
      )}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Card
// ---------------------------------------------------------------------------

export interface CatalogCardProps {
  item: CatalogItem;
  onFavoriteToggle: (tenantReportId: string, currentValue: boolean) => void;
  isFavoritePending?: boolean;
}

export function CatalogCard({
  item,
  onFavoriteToggle,
  isFavoritePending = false,
}: CatalogCardProps) {
  const titleEs = item.title.es ?? item.code;

  return (
    <Link
      to="/reports/$id"
      params={{ id: item.tenant_report_id }}
      data-testid="catalog-card"
      className={cn(
        'group block rounded-[var(--radius-card)] border border-[var(--color-qup-border-dark)]',
        'bg-[var(--color-qup-bg-card-dark)] p-5 shadow-[var(--shadow-card)]',
        'transition-all duration-300',
        'hover:shadow-[var(--shadow-card-hover)] hover:-translate-y-[3px]',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-qup-primary)]',
      )}
    >
      {/* Header: title + favorite */}
      <div className="mb-3 flex items-start justify-between gap-2">
        <h3 className="text-sm font-semibold leading-snug text-[var(--color-qup-text-dark)] line-clamp-2">
          {titleEs}
        </h3>
        <FavoriteButton
          isFavorite={item.is_favorite}
          onToggle={() => onFavoriteToggle(item.tenant_report_id, item.is_favorite)}
          isPending={isFavoritePending}
        />
      </div>

      {/* Badges row */}
      <div className="mb-4 flex flex-wrap gap-1.5">
        <KindBadge kind={item.kind} />
        <CategoryBadge category={item.category} />
        <CapabilityBadge level={item.capabilities.level} />
        {item.version_status === 'update_available' && <VersionUpdateBadge />}
      </div>

      {/* Last execution */}
      <div className="text-xs text-[var(--color-qup-text-muted-dark)]">
        {item.last_execution !== undefined ? (
          <div className="flex items-center justify-between gap-2">
            <span>Última ejecución: {formatRelativeTime(item.last_execution.executed_at)}</span>
            {item.last_execution.row_count > 0 && (
              <span className="flex-shrink-0">
                {item.last_execution.row_count.toLocaleString('es')} filas
              </span>
            )}
          </div>
        ) : (
          <span>Nunca ejecutado</span>
        )}
      </div>
    </Link>
  );
}
